import type { Money } from "@empite/api-client";
import { formatMoney } from "@empite/api-client";
import { AppIcon } from "./app-icon";
import type { AppIdentity } from "./app-identity";
import { Button } from "./button";
import { Card } from "./card";
import { StatusPill, outcomeTone } from "./status-pill";

/**
 * One application's wallet, as a card in the wallets grid.
 *
 * Money is the ONLY place this port departs from the design's shape on
 * purpose. The design's `Money` was `{ minor: number }` and its `formatMoney`
 * divided by 100; the platform sends `micros` as a STRING because a balance in
 * micros leaves float64's exact range, so the amounts here are the app's
 * `Money` and the app's `formatMoney` — see the money module's own note.
 *
 * The design also hard-coded a green "active" pill. A frozen wallet drawn in
 * green is a lie about whether a request will be served, so the status is a
 * prop and its tone comes from `outcomeTone`.
 */
export function WalletCard({
  app,
  available,
  held,
  status,
}: {
  app: AppIdentity;
  available: Money;
  held: Money;
  /** e.g. `active`, `frozen`. */
  status: string;
}) {
  return (
    <Card className="flex flex-col overflow-hidden transition-shadow duration-200 ease-out-soft hover:shadow-raised">
      <div className="flex items-center gap-4 p-5 sm:p-6">
        <AppIcon initial={app.initial} tone={app.tone} size="lg" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-lg font-bold tracking-[-0.01em] text-ink">
            {app.name}
          </h3>
          {app.slug ? (
            <p className="truncate font-mono text-2xs text-ink-4">{app.slug}</p>
          ) : null}
        </div>
        <StatusPill tone={outcomeTone(status)} dot>
          {status}
        </StatusPill>
      </div>

      <div className="mx-5 border-t border-line-soft sm:mx-6" />

      <dl className="grid gap-3 p-5 sm:p-6">
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-xs font-medium text-ink-3">Available</dt>
          <dd className="tnum font-display text-xl font-bold text-success-ink">
            {formatMoney(available)}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-xs font-medium text-ink-3">
            Held for requests in flight
          </dt>
          <dd className="tnum text-md font-bold text-ink">{formatMoney(held)}</dd>
        </div>
      </dl>

      <div className="mt-auto flex flex-wrap gap-2 p-5 pt-0 sm:p-6 sm:pt-0">
        <Button href={`/wallets/${app.id}`} size="sm" className="flex-1 sm:flex-none">
          Add credit
        </Button>
        {/* The design linked `/usage?app=<slug>`. This app's `/usage` declares
            no `validateSearch`, so an app filter would be silently dropped —
            the link goes to the unfiltered dashboard until that filter exists. */}
        <Button
          href="/usage"
          variant="secondary"
          size="sm"
          className="flex-1 sm:flex-none"
        >
          View usage
        </Button>
      </div>
    </Card>
  );
}
